import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Product } from '../types';
import { getProducts } from '../services/api';
import ProductCard from '../components/product/ProductCard';
import AddProductDialog from '../components/product/AddProductDialog';
import EditProductDialog from '../components/product/EditProductDialog';

// <-- Импортируем новые стилизованные компоненты
import { MainLayoutContainer, Sidebar, ResponsiveCardGrid } from '../components/common/ResponsiveLayout';

const ProductsPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddDialogOpen, setAddDialogOpen] = useState(false);
    const [activeLetter, setActiveLetter] = useState('');
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const queryClient = useQueryClient();

    const { data: products = [], isLoading, error } = useQuery<Product[]>({
        queryKey: ['products'],
        queryFn: getProducts,
    });

    const { sortedProducts, letters } = useMemo(() => {
        const sorted = [...products].sort((a, b) => a.name.localeCompare(b.name));
        const uniqueLetters = Array.from(new Set(sorted.map(p => p.name[0].toUpperCase())));
        return { sortedProducts: sorted, letters: uniqueLetters };
    }, [products]);

    const filteredProducts = sortedProducts.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesLetter = activeLetter ? p.name.toUpperCase().startsWith(activeLetter) : true;
        return matchesSearch && matchesLetter;
    });

    return (
        // <-- Используем стилизованный компонент для основного контейнера
        <MainLayoutContainer>
            {/* <-- Используем стилизованный компонент для боковой панели */}
            <Sidebar component={Paper} sx={{ p: 1 }}>
                <Typography variant="h6" sx={{ p: 1 }}>
                    {t('products.index')}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <List dense>
                    <ListItemButton selected={!activeLetter} onClick={() => setActiveLetter('')}>
                        <ListItemText primary={t('products.all_products')} />
                    </ListItemButton>
                    {letters.map(letter => (
                        <ListItemButton key={letter} selected={activeLetter === letter}
                                        onClick={() => setActiveLetter(letter)}>
                            <ListItemText primary={letter} />
                        </ListItemButton>
                    ))}
                </List>
            </Sidebar>

            <Box component="main" sx={{ flexGrow: 1 }}>
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Typography variant="h4" component="h1">
                            {t('products.title')}
                        </Typography>
                        <Button variant="contained" color="primary" startIcon={<AddIcon />}
                                onClick={() => setAddDialogOpen(true)}>
                            {t('products.add_product')}
                        </Button>
                    </Box>

                    <TextField
                        fullWidth
                        label={t('products.search_placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 4 }}
                    />

                    {isLoading &&
                        <Box display="flex" justifyContent="center" mt={4}>
                            <CircularProgress color="primary" />
                        </Box>
                    }

                    {error && (
                        <Alert severity="error">
                            {(error as Error).message}
                        </Alert>
                    )}

                    {!isLoading && !error && filteredProducts.length === 0 && (
                        <Paper sx={{ textAlign: 'center', p: 4 }}>
                            <Typography variant="h6">
                                {t('products.no_products_found')}
                            </Typography>
                            <Typography color="text.secondary">
                                {t('products.adjust_search_filters')}
                            </Typography>
                        </Paper>
                    )}

                    {/* <-- Используем стилизованный компонент для сетки */}
                    <ResponsiveCardGrid>
                        {filteredProducts.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onEdit={setEditingProduct}
                            />
                        ))}
                    </ResponsiveCardGrid>
                </>
            </Box>

            <AddProductDialog
                open={isAddDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['products'] });
                }}
            />
            <EditProductDialog
                open={!!editingProduct}
                product={editingProduct}
                onClose={() => setEditingProduct(null)}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['products'] });
                    setEditingProduct(null);
                }}
            />
        </MainLayoutContainer>
    );
};

export default ProductsPage;
